import {useTestObservationsInfiniteQuery} from './useTestObservationsQuery';
import {useTestObservationsSearch} from './useTestObservationsSearch';
import {useTestObservationsFilter} from './useTestObservationsFilter';
import {TestObservation} from '../types';
import {
    buildCascades,
    CascadeEntry,
    ObservationSummary,
    summarizeObservations,
} from '../utils/buildCascades';
import {
    CascadeRow,
    flattenCascades,
} from '../utils/cascadeRows';

// Client-side keyword match. The server-side filter covers the structured fields
// (outcome, actionCode, ...); the keyword is the free-text sweep across the loaded
// rows, including the error text — "show me everything mentioning sign-in".
const matchesKeyword = (item: TestObservation, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.actionCode,
        item.syntheticUserId,
        item.outcome,
        item.verificationResult,
        item.error,
        ...(item.dependedOn ?? []),
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useTestObservations = () => {
    const {filters} = useTestObservationsFilter();
    const query = useTestObservationsInfiniteQuery(filters);

    // The keyword comes from the module-scoped store, so the value typed into the
    // toolbar in a SIBLING component actually reaches this filter (FNP-815).
    const {keyword} = useTestObservationsSearch();

    const items: TestObservation[] = (query.data?.pages?.flatMap(page => page?.items ?? []) ?? [])
        .filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    const visibleItems = items.filter(item => matchesKeyword(item, needle));

    // Cascades are built from the VISIBLE rows, so a keyword that hides a root also
    // releases its dependents to stand on their own (each then reports the
    // dependency it was waiting on) rather than silently vanishing with the parent.
    const cascades: CascadeEntry[] = buildCascades(visibleItems);
    // The flat, indented row list the table actually renders — a root immediately
    // followed by the actions it blocked.
    const rows: CascadeRow[] = flattenCascades(cascades);
    const summary: ObservationSummary = summarizeObservations(visibleItems);

    return {
        items,
        visibleItems,
        cascades,
        rows,
        summary,
        loading: query.isLoading,
        isError: query.isError,
        nextToken: query.hasNextPage ? 'has-more' : null,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
